import React, { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl';
import { SortingRule } from 'react-table';
import { Col, Row } from 'reactstrap';
import Toast from "../../helpers/Toast";
import { AssistancePartListDTO } from '../../models/AssistanceParts/AssistancePartListDTO';
import { PaginatedPartListDTO } from '../../models/AssistanceParts/PaginatedPartListDTO';
import { AssistanceService } from '../../services/AssistanceService';
import ButtonComponent from '../ButtonComponent';
import TableComponent from '../TableComponent';
import { PartService } from '../../services/PartService';
import { useAvailableModulesContext } from '../../context/AvailableModulesContext';
import { Utilities } from '../../helpers/Utilities';

type PartsNewRequestComponentType = {
    assistanceId: number;
    mode: "workshop" | "backoffice";
    setActiveTab: React.Dispatch<React.SetStateAction<number>>;
    reload: boolean;
    isComplete: boolean;
    isAcceptedBudget: boolean;
};


const PartNewRequestComponent = ({
    mode,
    assistanceId,
    setActiveTab,
    reload,
    isComplete,
    isAcceptedBudget
}: PartsNewRequestComponentType) => {

    const assistanceService = new AssistanceService();
    const partService = new PartService();
    const [isLoadingAssistance, setIsLoadingAssistance] = useState<boolean>(false);
    const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);
    const intl = useIntl();
    const [goToPage, setGoToPage] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const { availableModules, pageSizeOptions } = useAvailableModulesContext();

    // Define os tamanhos de página padrão
    const defaultPageSizes: number[] = [5, 15, 30];

    // Usa o Utilities para obter os tamanhos de página com base nas configurações
    const pageSizes: number[] = Utilities.GetPageSizeFromSettings(defaultPageSizes, pageSizeOptions);

    // Usa o Utilities para obter o tamanho de página inicial
    const [pageSize, setPageSize] = useState<number>(
        Utilities.GetInitialPageSize(pageSizes, pageSizeOptions, defaultPageSizes[0])
    );
    const [reloaddAfterRemove, setReloadAfterRemove] = useState<boolean>(false);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [parts, setParts] = useState<AssistancePartListDTO[]>([]);
    const isLocked = isAcceptedBudget || isComplete;
    const [expandedRows, setExpandedRows] = useState<{ [key: number]: boolean }>({});

    const toggleRow = (id: number) => {
        setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const RequestParts = async () => {
        setIsLoadingTable(true)
        var response = await assistanceService.RequestPartToWarehouse(assistanceId);

        if (response.success === false) {
            setIsLoadingTable(false);
            Toast.Show("error", response.message);
            return;
        }

        setIsLoadingTable(false);
        Toast.Show("success", "Peças pedidas com sucesso");
        setActiveTab(1)
    }

    const Remove = async (selectedPart: any) => {
        setIsLoadingTable(true);
        setReloadAfterRemove(true);
        var response = await assistanceService.RemoveSelectedPart(selectedPart.id);
        if (response.success === false) {
            setIsLoadingTable(false);
            Toast.Show("error", response.message);
            return
        }
        setIsLoadingTable(false);
        Toast.Show("success", "Peça removida da assistência com sucesso");
        setReloadAfterRemove(false);
    }

    const getRequestParts = async (currentPage: number, pageSize: number, sortBy: SortingRule<object>[]) => {
        if (assistanceId === 0) {
            return;
        }

        var data: PaginatedPartListDTO = {
            assistanceId: assistanceId,
            currentPage: currentPage,
            pageSize: pageSize,
        };

        setIsLoadingTable(true);
        var response = await assistanceService.GetSelectedParts(data);

        if (response.success === false) {
            setIsLoadingTable(false);
            Toast.Show("error", response.message);
            return;
        }

        setCurrentPage(currentPage);
        setPageSize(pageSize);
        setTotalPages(response.totalPages);
        setIsLoadingTable(false);
        setParts(response.items);
        setGoToPage(0);

        if (response.totalPages > 0 && currentPage > response.totalPages) {
            setGoToPage(1);
        }
    };

    useEffect(() => {
        getRequestParts(currentPage, pageSize, [])
    }, [reload, reloaddAfterRemove]);

    const columns = React.useMemo(
        () => [
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Code" />,
                accessor: "erpId",
                width: 25,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Name" />,
                accessor: "name",
                width: 35,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.Quantity" />,
                accessor: "quantity",
                width: 35,
            },
            {
                Header: <FormattedMessage id="Screens.Assistances.Parts.Table.PartDescription" />,
                accessor: "requestPartDescription",
                Cell: (props: any) => {
                    const isExpanded = expandedRows[props.cell.row.original.id];
                    return (
                        <div style={{ display: "inline-flex", height: "30px" }}>
                            <ButtonComponent
                                text={isExpanded ? intl.formatMessage({ id: "ExecutionsTableComponent.Close" }) : intl.formatMessage({ id: "ExecutionsTableComponent.Open" })}
                                onClick={() => toggleRow(props.cell.row.original.id)}
                            />
                        </div>
                    );
                },
                width: 10,
            },
            {
                Header: "",
                accessor: " ",
                Cell: (props: any) => {
                    return (
                        <div style={{ display: "inline-flex", height: "30px" }}>
                            <ButtonComponent
                                text={intl.formatMessage({ id: "PartNewRequestComponent.Remove" })}
                                disabled={isLocked === false ? isLoadingTable : true}
                                onClick={() => {
                                    Remove(props.cell.row.original)
                                }} />
                        </div>
                    );
                },
                width: 25,
            },
        ],
        [],
    );

    const renderRowSubComponent = (row: any) => {
        const part = row.original;
        return (
            <tr>
                <td colSpan={columns.length}>
                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div style={{ display: "flex" }}>
                            <div style={{ width: "100%", padding: "0.5rem", textAlign: "left" }}>
                                <p style={{ marginBottom: "8px" }}><strong>Observações:</strong></p>
                                {part.requestPartDescription}
                            </div>
                        </div>
                    </div>
                </td>
            </tr>
        );
    };

    return (
        <>
            <Row style={{ margin: "1rem 0px", display: "flex", justifyContent: "end", alignItems: "center" }}>
                <Col xl={3} lg={3} sm={3} xs={12} style={{ padding: "0px" }}>
                    <ButtonComponent
                        style={{ width: '100%' }}
                        onClick={() => {
                            RequestParts()
                        }}
                        text={intl.formatMessage({ id: "Screens.Assistances.Parts.Label.SendEmail" })}
                        loading={isLoadingAssistance}
                        disabled={isLocked}
                    />
                </Col>
            </Row>
            <TableComponent
                goToPage={goToPage}
                loading={isLoadingTable}
                pageCount={totalPages}
                columns={columns}
                manualPagination={true}
                fetchData={getRequestParts}
                pageSizes={pageSizes}
                initialPage={currentPage}
                initialPageSize={pageSize}
                initialSortBy={[]}
                data={parts}
                dontOrderFirstCollumn={true}
                disableSortBy={true}
                renderRowSubComponent={renderRowSubComponent}
                expandedRows={expandedRows}
            />
        </>
    )
}

export default PartNewRequestComponent